<script setup>
import { inject, ref, computed, onMounted, nextTick, onServerPrefetch } from 'vue'
import { useRoute } from 'vue-router'
import { useIntersectionObserver, useEventListener, useThrottleFn } from '@vueuse/core'
import { useHead } from '@unhead/vue'
import { getArticleList } from '@/api/article.js'
import { getVideoList } from '@/api/video.js'
import { checkVideo, appendUtmParams } from '@/utils'

const SSR = import.meta.env.SSR ? true : false
const route = useRoute()
const size = inject('size')
const categoryInfo = inject('categoryInfo')
const setAdParams = inject('setAdParams')
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
// 视频分类使用 position 13，文章分类使用 position 8
const adPosition = computed(() => (categoryInfo?.value?.is_video ? 13 : 8))
const head = ref({})
const { patch } = useHead(head)
// 面包屑
const breadcrumbs = computed(() => {
  return [
    {
      name: '主頁',
      path: '/',
      seoLink: import.meta.env.SSR ? import.meta.env.VITE_HOST_URL : location.origin
    },
    {
      name: categoryInfo.value?.parent?.title,
      path: categoryInfo.value?.parent?.link,
      seoLink: categoryInfo.value?.parent?.link
    },
    {
      name: categoryInfo.value?.title,
      path: '',
      seoLink: categoryInfo.value?.link
    }
  ]
})
const setSeo = () => {
  head.value.script = [
    {
      id: 'category-ld-json',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.value.map((v, i) => {
          return {
            '@type': 'ListItem',
            position: i + 1,
            name: v.name,
            item: v.seoLink
          }
        })
      })
    }
  ]
  if (!SSR) {
    // TC-1631: canonical 及 SEO 标签使用不含 utm 的干净 URL
    const searchParams = new URLSearchParams(window.location.search)
    const cleanParams = new URLSearchParams()
    searchParams.forEach((value, key) => {
      if (!key.startsWith('utm_')) cleanParams.set(key, value)
    })
    const cleanSearch = cleanParams.toString() ? `?${cleanParams.toString()}` : ''
    const cleanUrl = categoryInfo.value.link + cleanSearch
    head.value.link = [
      {
        rel: 'canonical',
        id: 'canonical',
        href: cleanUrl
      },
      {
        rel: 'alternate',
        hreflang: 'x-default',
        id: 'alternate-x-default',
        href: cleanUrl
      },
      {
        rel: 'alternate',
        hreflang: 'zh-hk',
        id: 'alternate-zh-hk',
        href: cleanUrl
      }
    ]
    head.value.meta = [
      {
        key: 'og-url',
        property: 'og:url',
        content: cleanUrl
      },
      {
        key: 'twitter-url',
        name: 'twitter:url',
        content: cleanUrl
      }
    ]
  }
  patch(head.value)
}

// TC-1582: SEO_Infinite Scroll Page Enhancement
const cardRef = ref([])
// 计算出最多出现次数的值
const mostFrequent = computed(() => {
  if (!cardRef.value) return null
  let visibleArr = cardRef.value.filter((v) => v.visible)
  let pageArr = visibleArr.map((v) => v.el.dataset.page)
  const frequency = {}
  let maxCount = 0
  let mostFrequent = null

  for (const item of pageArr) {
    frequency[item] = (frequency[item] || 0) + 1
    if (frequency[item] > maxCount) {
      maxCount = frequency[item]
      mostFrequent = item
    }
  }

  return {
    value: mostFrequent,
    count: maxCount
  }
})
// 生成帶有頁數的URL
const throttledFn = useThrottleFn(
  () => {
    if (cardRef.value && mostFrequent.value.value) {
      // TC-1631: 保留 utm 参数在地址栏
      window.history.replaceState(
        history.state,
        null,
        appendUtmParams(
          location.origin +
            location.pathname +
            (mostFrequent.value.value == 1 ? '' : `?page=${mostFrequent.value.value}`)
        )
      )
      setSeo()
    }
  },
  600,
  true
)
useEventListener('scroll', throttledFn)

// 監聽往上加載更多
const scrollerHeader = ref([])
const scrollerHeaderOne = computed(() => scrollerHeader.value[5])
const shOb = useIntersectionObserver(
  scrollerHeaderOne,
  ([entry], observerElement) => {
    if (entry?.isIntersecting && prevPage.value > 1) onload('prev')
  },
  { immediate: false, threshold: 1 }
)
// 監聽往下加載更多
const scrollerFooter = ref()
const sfOb = useIntersectionObserver(
  scrollerFooter,
  ([entry], observerElement) => {
    if (entry?.isIntersecting && !finished.value) onload('next')
  },
  { immediate: false }
)
// 模仿丝滑滚动
const toScroll = (_page, id) => {
  try {
    window.scrollBy({
      top:
        document.querySelector(`.page${_page}-${id}`).getBoundingClientRect().top -
        (isMobile.value ? 84 : 64)
    })
  } catch (error) {
    console.log(error)
  }
}

const initLoading = ref(true)
const loading = ref(false)
const isEmpty = computed(() => list.value.length === 0)
const list = ref([])
const finished = ref(false) // 最後頁完成狀態
const prevPage = ref(route.query.page || 0)
const page = ref(route.query.page || 1)
const limit = ref(9)
const onload = async (mode) => {
  const isInit = mode === 'init'
  const isPrev = mode === 'prev'
  if (loading.value) return
  if (!isInit) loading.value = true
  try {
    let _page = isPrev ? prevPage.value - 1 : page.value
    const listFunc = categoryInfo.value.is_video ? getVideoList : getArticleList
    const res = await listFunc({
      category_id: categoryInfo.value.id,
      page: _page,
      limit: limit.value
    })
    if (res.code !== 0) throw new Error(res.message)
    let newsList = res.data?.list || []
    newsList.forEach((val) => {
      val.page = _page
    })
    if (isInit) list.value = newsList
    else {
      if (isPrev) list.value.unshift(...newsList)
      else list.value.push(...newsList)
    }

    if (newsList.length < limit.value) finished.value = true
    else {
      if (isPrev) prevPage.value--
      else page.value++
    }

    if (isInit) {
      initLoading.value = false
      nextTick(() => sfOb.resume())
      if (route.query.page > 1 && newsList.length) {
        nextTick(() => {
          toScroll(_page, newsList[0].id)
          shOb.resume()
        })
      }
    } else {
      loading.value = false
      if (isPrev && newsList.length) {
        nextTick(() => toScroll(_page, newsList[newsList.length - 1].id))
      }
    }
  } catch (error) {
    console.warn(error)
    if (isInit) initLoading.value = false
    else loading.value = false
  }
}

setAdParams({
  content_type: 'content',
  sub_category_path: categoryInfo?.value?.path || 'x'
})

const loadSetSeo = async () => {
  await onload('init')
  setSeo()
}
onMounted(loadSetSeo)
onServerPrefetch(loadSetSeo)
</script>

<template>
  <div
    class="tvb-article-sub-category"
    :class="{ 'error-page': !initLoading && isEmpty, [size]: true }"
  >
    <template v-if="initLoading || isEmpty">
      <div v-if="initLoading" class="tvb-loading-box"><n-spin /></div>
      <tvb-empty v-else icon="img_article_deleted" des="抱歉，找不到相關資料"></tvb-empty>
    </template>
    <template v-else>
      <div class="tvb-article-sub-category-container">
        <div class="tvb-article-breadcrumb">
          <tvb-breadcrumb :breadcrumbs="breadcrumbs" :size="size"></tvb-breadcrumb>
        </div>

        <div v-if="initLoading" class="tvb-loading-box"><n-spin /></div>
        <template v-else>
          <div class="sub-category-content">
            <div class="model-h">
              <h1 class="clear-h title" :title="categoryInfo.title">{{ categoryInfo.title }}</h1>
            </div>
            <div class="model-content">
              <template v-if="prevPage > 1 && !SSR">
                <tvb-article-card
                  v-for="i in 6"
                  :key="i"
                  ref="scrollerHeader"
                  loading
                  :size="size"
                  :autoheight="isMobile"
                ></tvb-article-card>
              </template>
              <template v-for="(item, i) in list" :key="`${item.id}${item.page}`">
                <tvb-article-card
                  ref="cardRef"
                  :class="`page${item.page}-${item.id}`"
                  :size="size"
                  :autoheight="isMobile"
                  :img="item.image_info"
                  :isVideo="categoryInfo.is_video || checkVideo(item)"
                  :videoType="categoryInfo.is_video"
                  :playIcon="{ size: isMobile ? 40 : 48 }"
                  :title="item.title"
                  :subtitle="item.category_name"
                  :description="categoryInfo.is_video ? item.description : ''"
                  :date="item.publish_at"
                  :link="{ url: item.link, rel: item.link_rel, target: item.link_target }"
                  :other="{ page: item.page }"
                ></tvb-article-card>
                <tvb-ads
                  v-if="((i + 1) / 2) % 4 === 1 && !['s', 'xs'].includes(size) && list.length > 2"
                  :ad-type="'lrec'"
                  :position="adPosition"
                  class="tvb-article-card card-xl tvb-hairline--surround"
                  :unit="`/${categoryInfo?.path}`"
                  :params="{
                    content_type: 'content',
                    sub_category_path: categoryInfo?.path || 'x'
                  }"
                  style="
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    --tvb-hairline-border-radius: calc(2 * 8px);
                  "
                />
                <tvb-ads
                  v-if="(i + 1) % (['s', 'xs'].includes(size) ? 3 : 8) === 0 && list.length > 2"
                  :ad-type="['s', 'xs'].includes(size) ? 'bn' : 'sb'"
                  :key="['s', 'xs'].includes(size) ? 'bn' : 'sb'"
                  :position="adPosition"
                  class="tvb-article-card card-xl"
                  :style="{ gridColumn: `1 / span ${['s', 'xs'].includes(size) ? 1 : 3}` }"
                  :unit="`/${categoryInfo?.path}`"
                  :params="{
                    content_type: 'content',
                    sub_category_path: categoryInfo?.path || 'x'
                  }"
                />
              </template>
            </div>

            <div v-if="!finished" ref="scrollerFooter" class="tvb-loading-box loading-box">
              <n-spin />
            </div>
          </div>
        </template>
      </div>
      <tvb-ads
        :position="adPosition"
        :ad-type="'to'"
        :unit="`/${categoryInfo?.path}`"
        :params="{
          content_type: 'content',
          sub_category_path: categoryInfo?.path || 'x'
        }"
      />
      <!-- Sky Scraper 浮动侧边栏广告已移至 layout/index.vue 统一管理 -->
    </template>
  </div>
</template>

<style lang="scss" scoped>
.tvb-article-sub-category {
  // padding-top: 16px !important;

  .loading-box {
    padding: 16px 0;
  }

  .tvb-empty {
    height: 500px;
  }

  &.s,
  &.xs {
    .loading-box {
      padding: 10px 0;
    }

    .tvb-empty {
      height: 400px;
    }
  }

  .tvb-article-sub-category-container {
    max-width: var(--tvb-article-max-width);
    margin: 0 auto;
    margin-top: 8px;
  }

  .tvb-article-breadcrumb {
    margin-bottom: 24px;
  }

  .sub-category-content {
    .model-h {
      margin-bottom: 16px;

      .title {
        height: 32px;
        line-height: 32px;
        color: #231f20;
        font-size: 22px;
        font-weight: 500;
        letter-spacing: 0.5px;
        @include text-ellipsis();
      }
    }

    .model-content {
      --gap: 16px;
      --columns: 3;
      gap: var(--gap);
      display: grid;
      grid-template-columns: repeat(var(--columns), 1fr);

      :deep(.tvb-ads) {
        height: 100% !important;

        .tvb-ads_container {
          align-items: center;
        }
      }
    }
  }

  &.s,
  &.xs {
    .tvb-article-sub-category-container {
      margin-top: 0px;
    }
    .tvb-article-breadcrumb {
      margin-bottom: 12px;
    }

    .sub-category-content {
      .model-h {
        height: 24px;
        margin-bottom: 10px;

        .title {
          height: 24px;
          line-height: 24px;
          font-size: 17px;
        }
      }

      .model-content {
        --gap: 10px;
        --columns: 1;
      }
    }
  }
}
</style>
